import React, { useRef } from 'react';
import './HowItWorks.css';
import FlipBrochure from '../../components/FlipBrochure/FlipBrochure';
import ZoomableImage from '../../components/ZoomableImage/ZoomableImage';

const Hotspot = ({ number, top, left, label, onClick }) => (
  <button
    type="button"
    className="hotspot"
    style={{ top, left }}
    onClick={onClick}
    aria-label={label}
  >
    {number}
    <span className="hotspot-label">{label}</span>
  </button>
);

const HowItWorks = () => {
  const kitRef = useRef(null);
  const sensorRef = useRef(null);
  const configRef = useRef(null);
  const coreRef = useRef(null);
  const dataRef = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="how-it-works-container">
      {/* Hero Section */}
      <div
        className="how-hero-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/IMG_9401.jpg)` }}
      >
        <div className="how-hero-overlay">
          <h1 className="how-hero-title">
            How <span className="wordmark-pointe">Pointe</span><span className="wordmark-sense-white">Sense</span> Works
          </h1>
          <div className="how-hero-buttons">
            <button className="how-hero-button" onClick={() => scrollToSection(sensorRef)}>
              See How It Works
            </button>
          </div>
        </div>
      </div>

      {/* What's in the kit */}
      <section ref={kitRef} className="how-section">
        <div className="how-content-wrapper">
          <h2 className="how-heading">What&rsquo;s in the kit</h2>
          <p className="how-text">
            Each PointeSense kit is built for both feet: two PSFlex sensing inserts and two Cores,
            one pair per foot, all connecting to a single app on your phone or iPad. Tap a number
            below to jump straight to that part of how it works.
          </p>

          <div className="hotspot-image-wrap">
            <img
              src={`${process.env.PUBLIC_URL}/images/PS/PS_components.png`}
              alt="PointeSense kit components labeled."
              className="config-image config-image-bordered"
            />
            <Hotspot
              number={1}
              top="46%"
              left="24%"
              label="PSFlex: the technology"
              onClick={() => scrollToSection(sensorRef)}
            />
            <Hotspot
              number={2}
              top="46%"
              left="44%"
              label="PSFlex: form factors"
              onClick={() => scrollToSection(configRef)}
            />
            <Hotspot
              number={3}
              top="78%"
              left="60%"
              label="The Core: communication"
              onClick={() => scrollToSection(coreRef)}
            />
            <Hotspot
              number={4}
              top="25%"
              left="72%"
              label="The App: data and interpretation"
              onClick={() => scrollToSection(dataRef)}
            />
          </div>

          <ul className="hotspot-legend">
            <li><span className="hotspot-legend-number">1</span> PSFlex: the technology</li>
            <li><span className="hotspot-legend-number">2</span> PSFlex: form factors</li>
            <li><span className="hotspot-legend-number">3</span> The Core: communication</li>
            <li><span className="hotspot-legend-number">4</span> The App: data and interpretation</li>
          </ul>

          <FlipBrochure
            frontSrc={encodeURI(`${process.env.PUBLIC_URL}/images/Materials/Brochure Outside.png`)}
            backSrc={encodeURI(`${process.env.PUBLIC_URL}/images/Materials/Brochure Inside.png`)}
            frontAlt="PointeSense brochure, outside cover"
            backAlt="PointeSense brochure, inside spread"
          />
        </div>
      </section>

      {/* What is behind the sensor */}
      <section ref={sensorRef} className="how-section how-section-tint">
        <div className="how-content-wrapper">
          <h2 className="how-heading">What is behind the sensor?</h2>
          <p className="how-text">
            PSFlex, our sensing insert, takes advantage of the piezoresistive effect, a property of
            certain materials whose internal resistance changes in response to pressure. When your
            foot applies pressure to the sensor, the material compresses, causing a drop in
            resistance that we measure and convert into pressure and force readings. Piezoresistive
            sensing is built for sustained pressure loads, which makes it well suited to monitoring
            pressure inside a pointe shoe during a fitting or in the studio.
          </p>
          <p className="how-text">
            It&rsquo;s also proven technology: piezoresistive sensing already shows up in medical
            touch devices, plantar-pressure shoe insoles, and industrial pressure monitoring, anywhere
            steady, reliable pressure readings matter.
          </p>
        </div>
      </section>

      {/* Sensor construction */}
      <section ref={configRef} className="how-section">
        <div className="how-content-wrapper">
          <h2 className="how-heading">Built to fit inside the shoe</h2>
          <p className="how-text">
            We&rsquo;ve patented PSFlex as a thin film sensor, under 0.8mm thick, that easily folds into a 3D shape so it bends
            and conforms easily to the inside of a pointe shoe.
          </p>

          <img
            src={`${process.env.PUBLIC_URL}/images/PS/PS_sensor_3D.png`}
            alt="Sensor 3D shape/bend around a person's foot."
            className="config-image"
          />

          <p className="how-text">PSFlex comes in two configurations:</p>

          <div className="config-grid">
            <div className="config-card">
              <img
                src={`${process.env.PUBLIC_URL}/images/PS/PSFlexBox.jpg`}
                alt="PSFlex Box layout"
                className="config-image"
              />
              <h3 className="config-title">PSFlex Box</h3>
              <p className="config-subtitle">Toe Pad Area</p>
              <p className="config-count">64 sensor elements</p>
            </div>
            <div className="config-card">
              <img
                src={`${process.env.PUBLIC_URL}/images/PS/PSFlexFull.jpg`}
                alt="PSFlex Full layout"
                className="config-image"
              />
              <h3 className="config-title">PSFlex Full</h3>
              <p className="config-subtitle">Toe Pad and &#8532; Shank</p>
              <p className="config-count">88 sensor elements</p>
            </div>
          </div>

          <p className="how-text">
            Each sensing pad is only 4mm &times; 4mm, giving us fine spatial resolution, so we can
            see individual pressure points on your toes, metatarsal heads, and more.
          </p>
        </div>
      </section>

      {/* The Core */}
      <section ref={coreRef} className="how-section how-section-tint">
        <div className="how-content-wrapper">
          <h2 className="how-heading">From sensor to signal</h2>

          <img
            src={`${process.env.PUBLIC_URL}/images/PS/PS_cores.png`}
            alt="Two PointeSense Cores"
            className="config-image config-image-small"
          />

          <p className="how-text">
            Raw sensor readings need data acquisition to become useful. To make PSFlex readable, we built a wearable
            &ldquo;Core,&rdquo; now smaller than some smart watches, that straps around the arch of
            each foot. The Core is a Bluetooth device that connects to its PSFlex through a clip-in
            ribbon connector and handles all of the processing, amplification, filtering, and
            calibration needed to turn raw signal into readable data. Both Cores pair with the same
            app on a mobile device, streaming data from both feet in real time so a fitter or
            professional can watch it live, and the dancer can save and review it later. Each Core is
            wireless, rechargeable via USB-C, and runs for about 2 hours per charge.
          </p>

          <ZoomableImage
            src={`${process.env.PUBLIC_URL}/images/PS/PS_app.png`}
            alt="PointeSense app showing a live pressure heatmap and plotted sensor data"
            className="app-screenshot"
          />
        </div>
      </section>

      {/* Getting ready to dance */}
      <section className="how-section">
        <div className="how-content-wrapper">
          <h2 className="how-heading">Getting ready to dance</h2>
          <p className="how-text">
            PointeSense was designed to fit quickly into a dancer&rsquo;s existing routine. Each kit
            includes two PSFlex inserts, two Cores, a connector ribbon for each, and a USB-C
            charging cable. Steps 1 through 5 happen once per foot; both Cores then pair with the
            same app session.
          </p>

          <img
            src={`${process.env.PUBLIC_URL}/images/PS/PSFlex_Donning.png`}
            alt="Diagram showing the steps of putting on the PointeSense components."
            className="config-image"
          />

          <ol className="steps-list">
            <li>Insert PSFlex inside the pointe shoe&rsquo;s box and shank. It folds in like a box liner.</li>
            <li>Strap the Core around the arch of the foot.</li>
            <li>Put on any padding of your choice.</li>
            <li>Put your pointe shoe on and tie the ribbons, if any.</li>
            <li>Adjust the Core as needed and snap the sensor ribbon into the connector.</li>
            <li>Turn on both devices using the button on each Core.</li>
            <li>Open the app on your phone, tablet, or other mobile device, pair with both Cores, start recording, and start dancing.</li>
          </ol>
        </div>
      </section>

      {/* From data to insight */}
      <section ref={dataRef} className="how-section how-section-tint">
        <div className="how-content-wrapper">
          <h2 className="how-heading">From data to insight</h2>
          <p className="how-text">
            Turning PSFlex data into insight starts with people, not algorithms. Right now,
            we&rsquo;re focused on collecting data. Fitters, dance medicine specialists, and other
            professionals label what they see: the subtle changes that actually mean something for
            injury, comfort, or pain. That labeling is what lets machine learning find patterns in
            the data that people can&rsquo;t spot on their own, patterns too subtle or too spread
            across sessions and dancers to catch by eye.
          </p>
          <p className="how-text">
            We&rsquo;re still building the dataset it will take to get there. Any data we train on is
            collected only with consent and anonymized as much as possible.
          </p>
          <p className="how-text">
            We then work to translate those patterns back into dance-specific vocabulary and visuals
            that a dancer, teacher, or fitter can use to evaluate pointe shoe fit, technique, and
            pre-pointe readiness: things like strength going into pointe work, asymmetries between
            feet or within a fit that could lead to injury, and alignment that isn&rsquo;t safely
            supportive.
          </p>
          <p className="how-text">
            Even without any learning or training involved, we can already show:
          </p>
          <ul className="how-list">
            <li>Real-time updating pressure heatmaps of the dancer&rsquo;s toes, metatarsals, and shank</li>
            <li>Quantitative metrics and comparisons of loads across different sections of the foot, like platform versus toes, or top of foot versus sole</li>
            <li>Where pressure moves inside the shoe as you roll up and down</li>
            <li>Cleaned position and rotation information about the foot from the onboard IMU</li>
          </ul>

          <h3 className="how-subheading">Potential applications</h3>
          <ul className="how-list">
            <li>Quantitative metrics on how different models support different feet, from roll-through to pressure concentration, helping manufacturers better describe their shoes to dancers and fitters</li>
            <li>Quantitative comparisons across brands of how different pointe shoes fit the same dancer</li>
            <li>Data-based identification of specific fit issues behind ambiguous problems, like what&rsquo;s actually causing a dancer to sink in a shoe</li>
            <li>Tracking a pointe shoe&rsquo;s support as it degrades over time</li>
            <li>Research quantifying dancer biomechanics, foot-to-shoe relationships, and injury risk factors</li>
          </ul>
          <p className="how-text">Have an idea for another application? We&rsquo;d love to hear it.</p>
        </div>
      </section>

      {/* Learn more cards */}
      <section className="how-section">
        <div className="how-content-wrapper">
          <h2 className="how-heading">Learn more about pointe shoes</h2>
          <div className="cards-grid">
            <div className="graphic-card-wrapper">
              <img
                src={`${process.env.PUBLIC_URL}/anatomy_card.png`}
                alt="Anatomy of a pointe shoe"
                className="graphic-card"
              />
            </div>
            <div className="graphic-card-wrapper">
              <img
                src={`${process.env.PUBLIC_URL}/alignment_card.png`}
                alt="What is correct alignment?"
                className="graphic-card"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact note */}
      <section className="how-section how-section-tint how-contact-note">
        <div className="how-content-wrapper">
          <p className="how-text">
            For specific technical information or datasheets on PSFlex or the Core, please contact
            our Head of Technology, Kaitlyn, at{' '}
            <a href="mailto:kaitlyn@pointesense.com">kaitlyn@pointesense.com</a>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
